import React, { useState } from 'react';
import SEOHead from '../components/SEOHead';
import emailjs from '@emailjs/browser';

// ── Data ──────────────────────────────────────────────────────────────────────

const roles = [
  {
    id: 'fullstack',
    title: 'Full-Stack Developer',
    type: 'Full Time',
    location: 'Dehradun / Remote',
    department: 'Engineering',
    color: '#3B82F6',
    icon: '💻',
    urgent: true,
    summary: 'Work directly on HealHub, Suraksha, and upcoming products. Own features end-to-end — from architecture decisions to production deployment.',
    responsibilities: [
      'Build and maintain features across HealHub and Suraksha codebases',
      'Handle bugs, integrations, and performance optimisation',
      'Participate in architecture decisions for new product modules',
      'Write clean, documented, testable code — no cowboy commits',
      'Collaborate directly with the founder on product direction',
    ],
    requirements: [
      'Strong proficiency in React.js and Node.js or Laravel/Django',
      'Experience with mobile development (React Native preferred)',
      'Solid understanding of REST APIs and database design',
      'Familiarity with Firebase, AWS, or GCP',
      'Self-driven — you must be comfortable owning problems, not just tasks',
    ],
    niceToHave: [
      'Experience with AI/LLM API integration',
      'Prior experience at a product startup',
      'Contributions to open source projects',
    ],
  },
  {
    id: 'sales',
    title: 'Software Sales Executive',
    type: 'Full Time',
    location: 'Dehradun / Field',
    department: 'Sales',
    color: '#22C55E',
    icon: '🤝',
    urgent: true,
    summary: 'Close clinic deals for HealHub and pitch Suraksha to businesses. This is a commission-heavy role for someone who is genuinely hungry to sell software that makes a difference.',
    responsibilities: [
      'Close HealHub subscriptions with clinics, hospitals, and healthcare providers',
      'Pitch Suraksha to businesses, NBFCs, and enterprise clients',
      'Handle pricing objections, negotiations, and contract finalisation',
      'Maintain a full sales pipeline with daily activity tracking',
      'Provide market feedback directly to the product team',
    ],
    requirements: [
      'Minimum 2 years of B2B sales experience',
      'Experience in SaaS sales or medical rep background preferred',
      'Exceptional communication and negotiation skills',
      'Comfortable with CRM tools and structured sales processes',
      'Commission-driven mindset — base + uncapped variable',
    ],
    niceToHave: [
      'Existing network of clinic owners or hospital decision-makers',
      'Prior experience selling healthcare or fintech software',
      'Understanding of India\'s clinic ecosystem',
    ],
  },
  {
    id: 'leadgen',
    title: 'Lead Generation & BD Executive',
    type: 'Full Time',
    location: 'Dehradun / Hybrid',
    department: 'Business Development',
    color: '#F59E0B',
    icon: '📊',
    urgent: false,
    summary: 'The pipeline engine. Your job is to find clinics, businesses, and decision-makers every single day and feed warm leads to the sales team. Cold calling, WhatsApp outreach, email campaigns — all of it.',
    responsibilities: [
      'Identify and contact clinic owners, hospital administrators, and business decision-makers',
      'Execute daily cold call targets, WhatsApp outreach, and email sequences',
      'Research and build structured lead lists by geography and specialty',
      'Qualify leads and hand off to sales with full context notes',
      'Track all outreach activity in the CRM with disciplined data hygiene',
    ],
    requirements: [
      'Excellent spoken communication — confident cold caller',
      'Process-driven with ability to hit daily activity targets',
      'Comfortable with MS Excel, Google Sheets, and basic CRM tools',
      'Resilient — rejection is daily and you bounce back fast',
      'Junior profile welcome — attitude matters more than experience',
    ],
    niceToHave: [
      'Prior experience in tele-calling or lead generation',
      'Familiarity with Uttarakhand\'s healthcare geography',
      'Basic English writing for email outreach',
    ],
  },
  {
    id: 'customersuccess',
    title: 'Customer Success & Onboarding Manager',
    type: 'Full Time',
    location: 'Dehradun / Hybrid',
    department: 'Customer Success',
    color: '#A855F7',
    icon: '🎯',
    urgent: false,
    summary: 'The bridge between our product and our users. Train clinics on HealHub, handle support escalations, collect structured product feedback, and ensure every clinic gets full value from their subscription.',
    responsibilities: [
      'Onboard new HealHub clinics — setup, staff training, workflow configuration',
      'Handle inbound support calls and resolve issues within defined SLAs',
      'Conduct regular check-in calls to measure satisfaction and collect feedback',
      'Document product feedback and translate it into structured reports for the product team',
      'Identify upsell opportunities and flag churn risk proactively',
    ],
    requirements: [
      'Excellent communication skills in Hindi and English',
      'Patient, empathetic, and skilled at explaining technical concepts simply',
      'Organised — able to manage multiple clinic accounts simultaneously',
      'Basic technical aptitude — comfortable learning and explaining software',
      'Prior experience in customer service, training, or account management preferred',
    ],
    niceToHave: [
      'Experience in healthcare sector or with medical professionals',
      'Familiarity with clinic workflows and medical terminology',
      'Experience with helpdesk or CRM tools',
    ],
  },
];

const perks = [
  {
    icon: '🚀',
    title: 'Work on Real Products',
    desc: 'Not internal tools or client work. You will work on HealHub, Suraksha, and Vedant — products that go into the hands of real users.',
  },
  {
    icon: '🎯',
    title: 'Direct Access to the Founder',
    desc: 'No layers of management. You work directly with Ashish. Your ideas get heard. Your decisions matter from day one.',
  },
  {
    icon: '📈',
    title: 'Equity Conversation for the Right Fit',
    desc: 'We are a growing company. For exceptional talent, we are open to equity conversations. Build with us — own a piece of what we build.',
  },
  {
    icon: '🧠',
    title: 'Learn at Startup Speed',
    desc: 'You will do more in 6 months here than in 2 years at a large company. Broad ownership, fast cycles, and real-world problem solving.',
  },
  {
    icon: '🌄',
    title: 'Dehradun Quality of Life',
    desc: 'Work from a city with clean air, low cost of living, and zero commute stress. We believe great work does not require a metro address.',
  },
  {
    icon: '💰',
    title: 'Competitive Compensation',
    desc: 'Market-rate salary benchmarked against Tier 1 city standards. Sales roles have uncapped commission. Performance is rewarded without politics.',
  },
];

const culture = [
  { label: 'Company Size', value: 'Small, Focused Team' },
  { label: 'Decision Speed', value: 'Hours, not weeks' },
  { label: 'Meeting Culture', value: 'Minimal — async first' },
  { label: 'Work Style', value: 'Ownership, not tasks' },
  { label: 'Growth Path', value: 'Fast — tied to company growth' },
  { label: 'Funding', value: 'Bootstrapped — profitable focused' },
];

// ── Application Form ──────────────────────────────────────────────────────────

function ApplicationForm({ preselectedRole }: { preselectedRole: string }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: preselectedRole,
    experience: '',
    portfolio: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      setError('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await emailjs.send(
        'service_g9tvszg',
        'template_124o1la',
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone,
          company: `Job Application — ${formData.role}`,
          message: `
Job Application Received

Role: ${formData.role}
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Experience: ${formData.experience || 'Not specified'}
Portfolio/LinkedIn: ${formData.portfolio || 'Not provided'}

Cover Note:
${formData.message}
          `.trim(),
          to_name: 'Ashish Uniyal',
        },
        'u6gb-CCFGNTwlW3Ot'
      );
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please email your application directly to a.uniyal@redcubicalsystems.com');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(59,130,246,0.2)',
    borderRadius: '10px',
    padding: '14px 16px',
    fontSize: '14px',
    color: '#fff',
    fontFamily: 'Inter, sans-serif',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: '700',
    color: 'rgba(255,255,255,0.4)',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    display: 'block',
    marginBottom: '6px',
  };

  if (submitted) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '48px 32px',
        background: 'rgba(59,130,246,0.06)',
        border: '1px solid rgba(59,130,246,0.2)',
        borderRadius: '16px',
      }}>
        <p style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</p>
        <h3 style={{
          fontFamily: 'Barlow Condensed, sans-serif',
          fontSize: '26px', fontWeight: '900',
          color: '#fff', textTransform: 'uppercase',
          marginBottom: '10px',
        }}>
          Application Received!
        </h3>
        <p style={{
          fontSize: '14px',
          color: 'rgba(255,255,255,0.5)',
          lineHeight: '1.7',
        }}>
          Ashish will personally review your application
          and reach out within 48 hours if there is a fit.
          We review every single application — no automated rejections.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}
    >
      {/* Row 1 — Name + Phone */}
      <div className="careers-form-row">
        <div>
          <label style={labelStyle}>Your Name *</label>
          <input
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Full name"
            required
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#3B82F6')}
            onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
          />
        </div>
        <div>
          <label style={labelStyle}>Phone *</label>
          <input
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+91 98765 43210"
            required
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#3B82F6')}
            onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
          />
        </div>
      </div>

      {/* Row 2 — Email + Role */}
      <div className="careers-form-row">
        <div>
          <label style={labelStyle}>Email *</label>
          <input
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="you@example.com"
            required
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#3B82F6')}
            onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
          />
        </div>
        <div>
          <label style={labelStyle}>Role Applying For</label>
          <select
            name="role"
            value={formData.role}
            onChange={handleChange}
            style={{
              ...inputStyle,
              cursor: 'pointer',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%236B7280' d='M6 8L0 0h12z'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 16px center',
              paddingRight: '40px',
            }}
            onFocus={e => (e.target.style.borderColor = '#3B82F6')}
            onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
          >
            <option value="Full-Stack Developer" style={{ background: '#0A0E1A' }}>
              Full-Stack Developer
            </option>
            <option value="Software Sales Executive" style={{ background: '#0A0E1A' }}>
              Software Sales Executive
            </option>
            <option value="Lead Generation & BD Executive" style={{ background: '#0A0E1A' }}>
              Lead Generation & BD Executive
            </option>
            <option value="Customer Success & Onboarding Manager" style={{ background: '#0A0E1A' }}>
              Customer Success & Onboarding Manager
            </option>
          </select>
        </div>
      </div>

      {/* Row 3 — Experience + Portfolio */}
      <div className="careers-form-row">
        <div>
          <label style={labelStyle}>Years of Experience</label>
          <select
            name="experience"
            value={formData.experience}
            onChange={handleChange}
            style={{
              ...inputStyle,
              cursor: 'pointer',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%236B7280' d='M6 8L0 0h12z'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 16px center',
              paddingRight: '40px',
            }}
            onFocus={e => (e.target.style.borderColor = '#3B82F6')}
            onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
          >
            <option value="" style={{ background: '#0A0E1A' }}>Select experience</option>
            <option value="Fresher / 0-1 years" style={{ background: '#0A0E1A' }}>Fresher / 0-1 years</option>
            <option value="1-2 years" style={{ background: '#0A0E1A' }}>1-2 years</option>
            <option value="2-4 years" style={{ background: '#0A0E1A' }}>2-4 years</option>
            <option value="4-6 years" style={{ background: '#0A0E1A' }}>4-6 years</option>
            <option value="6+ years" style={{ background: '#0A0E1A' }}>6+ years</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Portfolio / LinkedIn / GitHub</label>
          <input
            name="portfolio"
            value={formData.portfolio}
            onChange={handleChange}
            placeholder="https://..."
            style={inputStyle}
            onFocus={e => (e.target.style.borderColor = '#3B82F6')}
            onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
          />
        </div>
      </div>

      {/* Cover note */}
      <div>
        <label style={labelStyle}>
          Why do you want to work at Redcubical? *
        </label>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Tell us what excites you about our products and mission. What makes you the right person for this role? Be honest — we value directness over polished corporate answers."
          required
          rows={5}
          style={{
            ...inputStyle,
            resize: 'vertical',
            minHeight: '130px',
          }}
          onFocus={e => (e.target.style.borderColor = '#3B82F6')}
          onBlur={e => (e.target.style.borderColor = 'rgba(59,130,246,0.2)')}
        />
      </div>

      {error && (
        <p style={{
          fontSize: '13px', color: '#E63946',
          background: 'rgba(230,57,70,0.08)',
          border: '1px solid rgba(230,57,70,0.2)',
          padding: '10px 14px', borderRadius: '8px',
        }}>
          ⚠️ {error}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="btn-primary"
        style={{
          width: '100%',
          justifyContent: 'center',
          padding: '16px',
          fontSize: '14px',
          opacity: submitting ? 0.7 : 1,
          cursor: submitting ? 'not-allowed' : 'pointer',
        }}
      >
        {submitting ? '⏳ Submitting...' : '🚀  Submit Application'}
      </button>

      <p style={{
        fontSize: '11px',
        color: 'rgba(255,255,255,0.25)',
        textAlign: 'center',
        lineHeight: '1.6',
      }}>
        Every application is personally reviewed by Ashish ·
        We respond within 48 hours
      </p>
    </form>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function Careers() {
  const [activeRole, setActiveRole] = useState(0);
  const [selectedRoleForForm, setSelectedRoleForForm] = useState(roles[0].title);

  const scrollToApply = (roleTitle: string) => {
    setSelectedRoleForForm(roleTitle);
    document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="page-wrapper" style={{ paddingTop: '80px' }}>
      <SEOHead
        title="Careers — Join Redcubical Systems"
        description="Join Redcubical Systems in Dehradun. We are hiring Full-Stack Developers, Sales Executives, Lead Generation specialists, and Customer Success managers. Work on HealHub, Suraksha, and Vedant."
        canonical="/careers"
        keywords="jobs Dehradun, software developer jobs Uttarakhand, careers Redcubical Systems, HealHub jobs, startup jobs Dehradun, sales executive healthcare software"
      />

      {/* ── HERO ── */}
      <section
        style={{
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          padding: '80px 32px',
          position: 'relative',
          overflow: 'hidden',
          background: `
            radial-gradient(ellipse 80% 60% at 50% -10%,
              rgba(37,99,235,0.4) 0%,
              rgba(29,58,138,0.2) 40%,
              transparent 70%),
            var(--black)
          `,
        }}
      >
        <div style={{
          position: 'absolute', top: '-200px', right: '-200px',
          width: '600px', height: '600px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(37,99,235,0.15) 0%, transparent 65%)',
          pointerEvents: 'none',
        }} />

        <div style={{
          maxWidth: '1280px', margin: '0 auto',
          width: '100%', position: 'relative',
        }}>
          <div className="careers-hero-grid">
            <div>
              <span className="badge" style={{ marginBottom: '32px' }}>
                📍 &nbsp; Dehradun, Uttarakhand · 4 Open Roles
              </span>

              <h1 className="display-heading" style={{ marginBottom: '24px' }}>
                BUILD WITH<br />
                <span className="gradient-text">PURPOSE.</span>
              </h1>

              <p style={{
                fontSize: 'clamp(16px, 2vw, 20px)',
                color: 'rgba(255,255,255,0.65)',
                lineHeight: '1.8',
                maxWidth: '560px',
                marginBottom: '24px',
              }}>
                We are a small team building software that protects lives, 
                digitises healthcare, and creates real change. If that excites you 
                more than a corporate job title — read on.
              </p>

              <p style={{
                fontSize: '15px',
                color: 'rgba(255,255,255,0.4)',
                lineHeight: '1.7',
                maxWidth: '520px',
                marginBottom: '40px',
              }}>
                No bureaucracy. No layers. Direct access to the founder. 
                Your work ships to real users in weeks — not quarters.
              </p>

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => {
                    document.getElementById('roles')
                      ?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="btn-primary"
                >
                  See Open Roles ↓
                </button>
                <button
                  onClick={() => {
                    document.getElementById('apply')
                      ?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="btn-outline"
                >
                  Apply Now →
                </button>
              </div>
            </div>

            {/* Right — Culture card */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{
                width: '100%', maxWidth: '440px',
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(59,130,246,0.2)',
                borderRadius: '24px',
                padding: '40px',
              }}>
                <p style={{
                  fontSize: '11px', fontWeight: '700',
                  color: 'rgba(255,255,255,0.3)',
                  letterSpacing: '2px', textTransform: 'uppercase',
                  marginBottom: '24px',
                }}>
                  What It Is Like Here
                </p>
                {culture.map((item) => (
                  <div key={item.label} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    padding: '14px 0',
                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                    gap: '16px',
                  }}>
                    <span style={{
                      fontSize: '12px',
                      color: 'rgba(255,255,255,0.35)',
                      flexShrink: 0,
                    }}>
                      {item.label}
                    </span>
                    <span style={{
                      fontSize: '13px',
                      color: 'rgba(255,255,255,0.75)',
                      fontWeight: '600',
                      textAlign: 'right',
                    }}>
                      {item.value}
                    </span>
                  </div>
                ))}

                <div style={{
                  marginTop: '24px',
                  padding: '16px',
                  background: 'rgba(34,197,94,0.06)',
                  border: '1px solid rgba(34,197,94,0.2)',
                  borderRadius: '12px',
                }}>
                  <p style={{
                    fontSize: '13px', fontWeight: '700',
                    color: '#22C55E', marginBottom: '4px',
                  }}>
                    ● 4 Roles Currently Open
                  </p>
                  <p style={{
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.35)',
                  }}>
                    Engineering · Sales · BD · Customer Success
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── OPEN ROLES ── */}
      <section
        id="roles"
        style={{
          padding: '120px 32px',
          background: 'var(--dark)',
        }}
      >
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '72px' }}>
            <span className="section-label">Open Positions</span>
            <h2 className="section-title" style={{ marginBottom: '20px' }}>
              4 ROLES.<br />
              <span className="gradient-text">IMMEDIATE JOINING.</span>
            </h2>
            <p className="section-subtitle" style={{ margin: '0 auto' }}>
              We hire for attitude first, skill second.
              If you see a role that excites you — apply. Even if you
              do not meet every requirement.
            </p>
          </div>

          <div className="roles-grid">

            {/* Left — Role selector */}
            <div style={{
              display: 'flex', flexDirection: 'column', gap: '8px',
            }}>
              {roles.map((role, i) => (
                <button
                  key={role.id}
                  onClick={() => setActiveRole(i)}
                  style={{
                    padding: '20px 24px',
                    background: activeRole === i
                      ? `${role.color}12`
                      : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${activeRole === i
                      ? role.color + '45'
                      : 'rgba(255,255,255,0.06)'}`,
                    borderRadius: '14px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.25s',
                    fontFamily: 'Inter, sans-serif',
                    width: '100%',
                  }}
                >
                  <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'flex-start', marginBottom: '8px',
                    gap: '8px',
                  }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                    }}>
                      <span style={{ fontSize: '20px' }}>{role.icon}</span>
                      <p style={{
                        fontSize: '15px', fontWeight: '700',
                        color: activeRole === i
                          ? '#fff' : 'rgba(255,255,255,0.55)',
                        transition: 'color 0.25s',
                      }}>
                        {role.title}
                      </p>
                    </div>
                    {role.urgent && (
                      <span style={{
                        fontSize: '10px', fontWeight: '700',
                        color: '#E63946',
                        background: 'rgba(230,57,70,0.1)',
                        border: '1px solid rgba(230,57,70,0.25)',
                        padding: '3px 10px', borderRadius: '50px',
                        letterSpacing: '0.5px', textTransform: 'uppercase',
                        flexShrink: 0,
                      }}>
                        Urgent
                      </span>
                    )}
                  </div>
                  <div style={{
                    display: 'flex', gap: '8px', flexWrap: 'wrap',
                  }}>
                    <span style={{
                      fontSize: '11px',
                      color: activeRole === i
                        ? role.color : 'rgba(255,255,255,0.3)',
                      fontWeight: '600',
                      transition: 'color 0.25s',
                    }}>
                      {role.department}
                    </span>
                    <span style={{
                      fontSize: '11px',
                      color: 'rgba(255,255,255,0.2)',
                    }}>·</span>
                    <span style={{
                      fontSize: '11px',
                      color: 'rgba(255,255,255,0.3)',
                    }}>
                      {role.location}
                    </span>
                  </div>
                </button>
              ))}
            </div>

            {/* Right — Role detail */}
            <div style={{
              background: 'rgba(255,255,255,0.02)',
              border: `1px solid ${roles[activeRole].color}25`,
              borderRadius: '24px',
              padding: '48px',
              position: 'relative',
              overflow: 'hidden',
            }}>
              {/* Background glow */}
              <div style={{
                position: 'absolute', top: '-80px', right: '-80px',
                width: '300px', height: '300px', borderRadius: '50%',
                background: `radial-gradient(circle, ${roles[activeRole].color}10 0%, transparent 70%)`,
                pointerEvents: 'none',
                transition: 'background 0.4s',
              }} />

              <div style={{ position: 'relative' }}>
                {/* Role header */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'flex-start', marginBottom: '24px',
                  flexWrap: 'wrap', gap: '16px',
                }}>
                  <div>
                    <div style={{
                      display: 'flex', alignItems: 'center',
                      gap: '12px', marginBottom: '8px',
                    }}>
                      <span style={{ fontSize: '28px' }}>
                        {roles[activeRole].icon}
                      </span>
                      <h3 style={{
                        fontFamily: 'Barlow Condensed, sans-serif',
                        fontSize: 'clamp(22px, 3vw, 32px)',
                        fontWeight: '900', color: '#fff',
                        textTransform: 'uppercase', lineHeight: 1,
                      }}>
                        {roles[activeRole].title}
                      </h3>
                    </div>
                    <div style={{
                      display: 'flex', gap: '12px', flexWrap: 'wrap',
                    }}>
                      {[
                        roles[activeRole].type,
                        roles[activeRole].location,
                        roles[activeRole].department,
                      ].map((tag) => (
                        <span key={tag} style={{
                          fontSize: '11px', fontWeight: '600',
                          color: 'rgba(255,255,255,0.4)',
                          background: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          padding: '4px 12px', borderRadius: '50px',
                          letterSpacing: '0.5px',
                        }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  {roles[activeRole].urgent && (
                    <span style={{
                      fontSize: '11px', fontWeight: '700',
                      color: '#E63946',
                      background: 'rgba(230,57,70,0.1)',
                      border: '1px solid rgba(230,57,70,0.25)',
                      padding: '6px 16px', borderRadius: '50px',
                      letterSpacing: '0.5px', textTransform: 'uppercase',
                    }}>
                      🔴 Urgent Hire
                    </span>
                  )}
                </div>

                {/* Summary */}
                <p style={{
                  fontSize: '15px',
                  color: 'rgba(255,255,255,0.6)',
                  lineHeight: '1.75',
                  marginBottom: '32px',
                  padding: '20px',
                  background: `${roles[activeRole].color}08`,
                  border: `1px solid ${roles[activeRole].color}20`,
                  borderRadius: '12px',
                }}>
                  {roles[activeRole].summary}
                </p>

                {/* Responsibilities */}
                <div style={{ marginBottom: '28px' }}>
                  <p style={{
                    fontSize: '11px', fontWeight: '800',
                    color: roles[activeRole].color,
                    letterSpacing: '2px', textTransform: 'uppercase',
                    marginBottom: '14px',
                  }}>
                    What You Will Do
                  </p>
                  {roles[activeRole].responsibilities.map((item, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'flex-start',
                      gap: '10px', marginBottom: '10px',
                      fontSize: '14px', color: 'rgba(255,255,255,0.6)',
                      lineHeight: '1.6',
                    }}>
                      <span style={{
                        color: roles[activeRole].color,
                        flexShrink: 0, marginTop: '2px',
                      }}>→</span>
                      {item}
                    </div>
                  ))}
                </div>

                {/* Requirements */}
                <div style={{ marginBottom: '28px' }}>
                  <p style={{
                    fontSize: '11px', fontWeight: '800',
                    color: 'rgba(255,255,255,0.4)',
                    letterSpacing: '2px', textTransform: 'uppercase',
                    marginBottom: '14px',
                  }}>
                    What We Need
                  </p>
                  {roles[activeRole].requirements.map((item, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'flex-start',
                      gap: '10px', marginBottom: '10px',
                      fontSize: '14px', color: 'rgba(255,255,255,0.6)',
                      lineHeight: '1.6',
                    }}>
                      <span style={{
                        color: '#22C55E',
                        flexShrink: 0, marginTop: '2px',
                      }}>✓</span>
                      {item}
                    </div>
                  ))}
                </div>

                {/* Nice to have */}
                <div style={{ marginBottom: '32px' }}>
                  <p style={{
                    fontSize: '11px', fontWeight: '800',
                    color: 'rgba(255,255,255,0.25)',
                    letterSpacing: '2px', textTransform: 'uppercase',
                    marginBottom: '14px',
                  }}>
                    Nice to Have
                  </p>
                  {roles[activeRole].niceToHave.map((item, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'flex-start',
                      gap: '10px', marginBottom: '8px',
                      fontSize: '14px', color: 'rgba(255,255,255,0.4)',
                      lineHeight: '1.6',
                    }}>
                      <span style={{
                        color: 'rgba(255,255,255,0.25)',
                        flexShrink: 0, marginTop: '2px',
                      }}>+</span>
                      {item}
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => scrollToApply(roles[activeRole].title)}
                  className="btn-blue"
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  Apply for {roles[activeRole].title} →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PERKS ── */}
      <section style={{
        padding: '120px 32px',
        background: 'var(--dark-2)',
        borderTop: '1px solid rgba(59,130,246,0.1)',
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '72px' }}>
            <span className="section-label">Why Join Us</span>
            <h2 className="section-title" style={{ marginBottom: '20px' }}>
              WHAT YOU GET BY<br />
              <span className="gradient-text">BUILDING WITH US</span>
            </h2>
          </div>

          <div style={{
            display: 'grid', gap: '2px',
            background: 'rgba(59,130,246,0.08)',
            border: '1px solid rgba(59,130,246,0.12)',
            borderRadius: '20px', overflow: 'hidden',
          }} className="perks-grid">
            {perks.map((perk, i) => (
              <div
                key={i}
                style={{
                  padding: '40px 36px',
                  background: 'rgba(5,8,16,0.85)',
                  transition: 'background 0.3s',
                  borderRight: i % 3 !== 2
                    ? '1px solid rgba(59,130,246,0.08)' : 'none',
                  borderBottom: i < 3
                    ? '1px solid rgba(59,130,246,0.08)' : 'none',
                }}
                onMouseEnter={e =>
                  (e.currentTarget.style.background = 'rgba(59,130,246,0.07)')
                }
                onMouseLeave={e =>
                  (e.currentTarget.style.background = 'rgba(5,8,16,0.85)')
                }
              >
                <div style={{
                  width: '52px', height: '52px',
                  background: 'rgba(59,130,246,0.1)',
                  border: '1px solid rgba(59,130,246,0.2)',
                  borderRadius: '12px',
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px', marginBottom: '20px',
                }}>
                  {perk.icon}
                </div>
                <h3 style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontSize: '20px', fontWeight: '900',
                  color: '#fff', textTransform: 'uppercase',
                  marginBottom: '10px',
                }}>
                  {perk.title}
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: 'rgba(255,255,255,0.5)',
                  lineHeight: '1.65',
                }}>
                  {perk.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── APPLICATION FORM ── */}
      <section
        id="apply"
        style={{
          padding: '120px 32px',
          background: `
            radial-gradient(ellipse 80% 60% at 50% 0%,
              rgba(59,130,246,0.1) 0%,
              transparent 70%),
            var(--black)
          `,
          borderTop: '1px solid rgba(59,130,246,0.15)',
        }}
      >
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div className="apply-grid">

            {/* Left — Copy */}
            <div>
              <span className="section-label">Apply Now</span>
              <h2 className="section-title" style={{ marginBottom: '24px' }}>
                TAKE THE<br />
                <span className="gradient-text">FIRST STEP</span>
              </h2>
              <p className="body-text" style={{ marginBottom: '32px', maxWidth: '440px' }}>
                Fill in the form. Be honest. Tell us why our mission 
                excites you. Every application is personally read by 
                Ashish — no automated screening, no ATS black holes.
              </p>

              <div style={{
                display: 'flex', flexDirection: 'column',
                gap: '16px', marginBottom: '40px',
              }}>
                {[
                  { icon: '⏱️', text: 'We respond within 48 hours — every time' },
                  { icon: '👤', text: 'Your application is read by the founder personally' },
                  { icon: '🎯', text: 'Interview process: 2 rounds max — no time wasting' },
                  { icon: '🚀', text: 'Joining timeline: As fast as 2 weeks' },
                ].map((item) => (
                  <div key={item.text} style={{
                    display: 'flex', alignItems: 'flex-start',
                    gap: '12px',
                  }}>
                    <span style={{ fontSize: '18px', flexShrink: 0 }}>
                      {item.icon}
                    </span>
                    <p style={{
                      fontSize: '15px',
                      color: 'rgba(255,255,255,0.6)',
                      lineHeight: '1.5',
                    }}>
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>

              {/* Direct contact */}
              <div style={{
                padding: '24px',
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '16px',
              }}>
                <p style={{
                  fontSize: '13px', fontWeight: '700',
                  color: 'rgba(255,255,255,0.4)',
                  textTransform: 'uppercase', letterSpacing: '1px',
                  marginBottom: '12px',
                }}>
                  Or reach us directly
                </p>
                <p style={{
                  fontSize: '15px',
                  color: 'rgba(255,255,255,0.6)',
                  marginBottom: '8px',
                }}>
                  📧 a.uniyal@redcubicalsystems.com
                </p>
                <p style={{
                  fontSize: '13px',
                  color: 'rgba(255,255,255,0.35)',
                }}>
                  Subject: Application — [Role Name]
                </p>
              </div>
            </div>

            {/* Right — Form */}
            <div style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(59,130,246,0.15)',
              borderRadius: '24px',
              padding: '48px 40px',
            }}>
              <p style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontSize: '22px', fontWeight: '900',
                color: '#fff', textTransform: 'uppercase',
                marginBottom: '8px',
              }}>
                Submit Your Application
              </p>
              <p style={{
                fontSize: '14px',
                color: 'rgba(255,255,255,0.4)',
                marginBottom: '28px',
              }}>
                All roles · Immediate joining · Dehradun based
              </p>
              <ApplicationForm preselectedRole={selectedRoleForForm} />
            </div>
          </div>
        </div>
      </section>

      {/* ── RESPONSIVE ── */}
      <style>{`
        .careers-hero-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: center;
        }
        .roles-grid {
          display: grid;
          grid-template-columns: 1fr 1.4fr;
          gap: 32px;
          align-items: start;
        }
        .perks-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
        }
        .apply-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: start;
        }
        .careers-form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        @media (max-width: 1024px) {
          .roles-grid {
            grid-template-columns: 1fr !important;
          }
          .perks-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 768px) {
          .careers-hero-grid {
            grid-template-columns: 1fr !important;
            gap: 48px !important;
          }
          .perks-grid {
            grid-template-columns: 1fr !important;
          }
          .apply-grid {
            grid-template-columns: 1fr !important;
            gap: 48px !important;
          }
          .careers-form-row {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}